import { useMemo } from 'react';
import { Link as RRDLink, useParams } from '@/providers/RouterShim';
import useMainNavigation from '../NavigationProvider';
import type { NavigationPage } from '../NavigationProvider/utils';
import useRouteNotifications, { getNotificationCountStartWithUrl } from '../useRouteNotifications';
import { Item, ItemIcon, ItemLabel, Notification } from './wrapper';

interface SideNavigationLinkProps {
  page: NavigationPage;
}

const SideNavigationLink = ({ page }: SideNavigationLinkProps) => {
  const { branchId } = useParams();
  const notifications = useRouteNotifications();
  const { expanded, onOpen, onToggleMobileMenu } = useMainNavigation();

  const count = useMemo(
    () =>
      page.quickNav?.resolveNotifications
        ? getNotificationCountStartWithUrl(
            notifications,
            branchId,
            page.quickNav?.resolveNotificationsPath || page.quickNav?.activeUrl || page.url,
          )
        : undefined,
    [page.quickNav?.resolveNotifications, page.quickNav?.activeUrl, page.url, notifications],
  );

  const to = useMemo(
    () =>
      (page.quickNav?.isActive || page.isActive) && !expanded
        ? location.href.replace(location.origin, '')
        : page.url,
    [page.quickNav?.isActive, page.isActive, page.url, expanded],
  );

  const onClick = () => {
    onOpen();

    const mainNavigationFoldout = document.getElementById('main-navigation-foldout');
    if (mainNavigationFoldout) {
      mainNavigationFoldout.focus();
    }

    if (page.quickNav?.shouldCloseMobileMenu) {
      onToggleMobileMenu();
    }
  };

  return (
    <Item
      key={`${page.url}-${page.title}`}
      as={RRDLink}
      to={to}
      title={page.quickNav?.title || page.title}
      onClick={onClick}
      data-testid={`side-navigation-${page.testId}`}
      $isActive={page.quickNav?.isActive || page.isActive}
    >
      <ItemIcon>
        {page.icon}
        {!!count && <Notification>{count > 99 ? '99+' : count}</Notification>}
      </ItemIcon>
      <ItemLabel>{page.quickNav?.title || page.title}</ItemLabel>
    </Item>
  );
};

export default SideNavigationLink;
